import { useEffect } from 'react';
import type React from 'react';

const DVD_SPEED_PX_PER_SEC = 220;
const DVD_OBSTACLE_PADDING_PX = 8;

type Rect = { left: number; top: number; right: number; bottom: number };
type State = { x: number; y: number; vx: number; vy: number };

// Constrains the starting angle to a 30°–60° diagonal in one of the four quadrants
// so the bird never launches on a purely horizontal or vertical trajectory (which
// looks flat and boring).
function pickInitialVelocity(): { vx: number; vy: number } {
    const baseAngle = Math.PI / 6 + Math.random() * (Math.PI / 6);
    const quadrant = Math.floor(Math.random() * 4);
    const angle = baseAngle + quadrant * (Math.PI / 2);
    return {
        vx: Math.cos(angle) * DVD_SPEED_PX_PER_SEC,
        vy: Math.sin(angle) * DVD_SPEED_PX_PER_SEC,
    };
}

function pickCorner(maxX: number, maxY: number): { x: number; y: number } {
    const corners = [[0, 0], [maxX, 0], [0, maxY], [maxX, maxY]] as const;
    const [x, y] = corners[Math.floor(Math.random() * corners.length)];
    return { x, y };
}

function measureObstacle(container: HTMLElement, obstacle: HTMLElement): Rect {
    const c = container.getBoundingClientRect();
    const o = obstacle.getBoundingClientRect();
    return {
        left: o.left - c.left - DVD_OBSTACLE_PADDING_PX,
        top: o.top - c.top - DVD_OBSTACLE_PADDING_PX,
        right: o.right - c.left + DVD_OBSTACLE_PADDING_PX,
        bottom: o.bottom - c.top + DVD_OBSTACLE_PADDING_PX,
    };
}

function resolveWallCollision(s: State, maxX: number, maxY: number): State {
    let { x, y, vx, vy } = s;
    if (x <= 0) { x = 0; vx = Math.abs(vx); }
    else if (x >= maxX) { x = maxX; vx = -Math.abs(vx); }
    if (y <= 0) { y = 0; vy = Math.abs(vy); }
    else if (y >= maxY) { y = maxY; vy = -Math.abs(vy); }
    return { x, y, vx, vy };
}

function resolveObstacleCollision(
    curr: State, prev: State, obs: Rect, dvdW: number, dvdH: number
): State {
    const overlapX = curr.x + dvdW > obs.left && curr.x < obs.right;
    const overlapY = curr.y + dvdH > obs.top && curr.y < obs.bottom;
    if (!(overlapX && overlapY)) { return curr; }

    const prevOverlapX = prev.x + dvdW > obs.left && prev.x < obs.right;
    const prevOverlapY = prev.y + dvdH > obs.top && prev.y < obs.bottom;

    let { x, y, vx, vy } = curr;
    if (!prevOverlapX && prevOverlapY) {
        if (vx > 0) { x = obs.left - dvdW; vx = -Math.abs(vx); }
        else { x = obs.right; vx = Math.abs(vx); }
    } else if (!prevOverlapY && prevOverlapX) {
        if (vy > 0) { y = obs.top - dvdH; vy = -Math.abs(vy); }
        else { y = obs.bottom; vy = Math.abs(vy); }
    } else {
        const pushLeft = x + dvdW - obs.left;
        const pushRight = obs.right - x;
        const pushUp = y + dvdH - obs.top;
        const pushDown = obs.bottom - y;
        const minPush = Math.min(pushLeft, pushRight, pushUp, pushDown);
        if (minPush === pushLeft) { x = obs.left - dvdW; vx = -Math.abs(vx); }
        else if (minPush === pushRight) { x = obs.right; vx = Math.abs(vx); }
        else if (minPush === pushUp) { y = obs.top - dvdH; vy = -Math.abs(vy); }
        else { y = obs.bottom; vy = Math.abs(vy); }
    }
    return { x, y, vx, vy };
}

function getRotationDeg(vx: number, vy: number): number {
    // The dvd sprite's default "forward" direction is upper-right
    // (atan2(-1, 1) = -PI/4), so add PI/4 to align with motion direction.
    return (Math.atan2(vy, vx) + Math.PI / 4) * (180 / Math.PI);
}

export function useDvdBounce(
    containerRef: React.RefObject<HTMLElement | null>,
    dvdRef: React.RefObject<HTMLElement | null>,
    obstacleRef: React.RefObject<HTMLElement | null>
) {
    useEffect(() => {
        const container = containerRef.current;
        const dvd = dvdRef.current;
        if (!container || !dvd) { return; }
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { return; }

        let { vx, vy } = pickInitialVelocity();
        let x = 0;
        let y = 0;
        let maxX = 0;
        let maxY = 0;
        let initialized = false;
        let obstacle: Rect | null = null;

        const recomputeBounds = () => {
            maxX = Math.max(0, container.clientWidth - dvd.clientWidth);
            maxY = Math.max(0, container.clientHeight - dvd.clientHeight);

            const obsEl = obstacleRef.current;
            obstacle = obsEl && container.clientWidth > 0 ? measureObstacle(container, obsEl) : null;

            if (!initialized && maxX > 0 && maxY > 0) {
                ({ x, y } = pickCorner(maxX, maxY));
                initialized = true;
            }
            if (x > maxX) { x = maxX; }
            if (y > maxY) { y = maxY; }
        };
        recomputeBounds();

        const resizeObserver = new ResizeObserver(recomputeBounds);
        resizeObserver.observe(container);
        resizeObserver.observe(dvd);
        if (obstacleRef.current) { resizeObserver.observe(obstacleRef.current); }

        let lastTime = performance.now();
        let rafId = 0;

        const step = (now: number) => {
            const dt = Math.min((now - lastTime) / 1000, 0.05);
            lastTime = now;

            const prev = { x, y, vx, vy };
            x += vx * dt;
            y += vy * dt;

            ({ x, y, vx, vy } = resolveWallCollision({ x, y, vx, vy }, maxX, maxY));

            if (obstacle) {
                ({ x, y, vx, vy } = resolveObstacleCollision(
                    { x, y, vx, vy }, prev, obstacle, dvd.clientWidth, dvd.clientHeight
                ));
            }

            dvd.style.transform = `translate(${x}px, ${y}px) rotate(${getRotationDeg(vx, vy)}deg)`;
            rafId = requestAnimationFrame(step);
        };

        rafId = requestAnimationFrame(step);
        return () => {
            cancelAnimationFrame(rafId);
            resizeObserver.disconnect();
        };
    }, [containerRef, dvdRef, obstacleRef]);
}
